// Tax (Impôts) progressive bracket helpers — extracted to avoid babel-visual-edits issues

export const TAX_BRACKETS = [
  { upTo: 11294, rate: 0, label: '0 € à 11 294 € — 0%' },
  { upTo: 28797, rate: 11, label: '11 295 € à 28 797 € — 11%' },
  { upTo: 82341, rate: 30, label: '28 798 € à 82 341 € — 30%' },
  { upTo: 177106, rate: 41, label: '82 342 € à 177 106 € — 41%' },
  { upTo: Infinity, rate: 45, label: 'Au-delà de 177 106 € — 45%' },
];

export const ACTIVITY_TYPES_TAX = [
  { id: 'service', label: 'Service' },
  { id: 'commerce', label: 'Commerce' },
  { id: 'restauration', label: 'Restauration' },
  { id: 'pme', label: 'PME' },
];

export const PME_REGIMES = [
  { id: 'is', label: "IS — Impôt sur les Sociétés" },
  { id: 'ir', label: "IR — Impôt sur le Revenu" },
];

// IS thresholds (PME taux réduit)
export const IS_REDUCED_THRESHOLD = 42500;
export const IS_REDUCED_RATE = 0.15;
export const IS_NORMAL_RATE = 0.25;

export const fmtEur = (n) => {
  const v = isFinite(n) ? n : 0;
  return v.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
};
export const numOr0 = (v) => {
  const n = parseFloat(v);
  return isFinite(n) ? n : 0;
};

export function progressiveTax(taxable) {
  if (!taxable || taxable <= 0) return { total: 0, slices: [] };
  let total = 0;
  let prev = 0;
  const slices = [];
  for (const b of TAX_BRACKETS) {
    if (taxable <= prev) break;
    const upper = Math.min(taxable, b.upTo);
    const slice = upper - prev;
    if (slice > 0) {
      const taxOnSlice = slice * (b.rate / 100);
      slices.push({ from: prev, to: upper, rate: b.rate, slice: slice, tax: taxOnSlice });
      total += taxOnSlice;
    }
    prev = b.upTo;
  }
  return { total: total, slices: slices };
}

function buildResultCard(title, total, breakdown, tone) {
  return { title: title, total: total, breakdown: breakdown, tone: tone };
}

// PME — IS
function computePmeIS(benefice) {
  const breakdown = ['Régime fiscal : IS (Impôt sur les Sociétés — PME)', 'Bénéfice annuel = ' + fmtEur(benefice)];
  let total = 0;
  if (benefice < IS_REDUCED_THRESHOLD) {
    total = benefice * IS_REDUCED_RATE;
    breakdown.push('Bénéfice < ' + fmtEur(IS_REDUCED_THRESHOLD) + ' → taux réduit 15%');
    breakdown.push('IS = ' + fmtEur(benefice) + ' × 15% = ' + fmtEur(total));
  } else {
    total = benefice * IS_NORMAL_RATE;
    breakdown.push('Bénéfice ≥ ' + fmtEur(IS_REDUCED_THRESHOLD) + ' → taux normal 25%');
    breakdown.push('IS = ' + fmtEur(benefice) + ' × 25% = ' + fmtEur(total));
  }
  breakdown.push('IMPÔT TOTAL = ' + fmtEur(total));
  return buildResultCard('IS — PME', total, breakdown, 'emerald');
}

// PME — IR
function computePmeIR(benefice, otherIncome) {
  const totalRevenu = benefice + otherIncome;
  const tax = progressiveTax(totalRevenu);
  const breakdown = [
    'Régime fiscal : IR (Impôt sur le Revenu — PME)',
    'Bénéfice annuel = ' + fmtEur(benefice),
  ];
  if (otherIncome > 0) {
    breakdown.push('+ Autres revenus = ' + fmtEur(otherIncome));
    breakdown.push('Total imposable = ' + fmtEur(benefice) + ' + ' + fmtEur(otherIncome) + ' = ' + fmtEur(totalRevenu));
  } else {
    breakdown.push('Total imposable = ' + fmtEur(totalRevenu));
  }
  for (const s of tax.slices) {
    const upperLabel = s.to === Infinity ? '∞' : fmtEur(s.to);
    breakdown.push('Tranche ' + fmtEur(s.from) + ' → ' + upperLabel + ' : ' + fmtEur(s.slice) + ' × ' + s.rate + '% = ' + fmtEur(s.tax));
  }
  breakdown.push('IMPÔT TOTAL = ' + fmtEur(tax.total));
  return buildResultCard('IR — PME', tax.total, breakdown, 'emerald');
}

// Auto-entrepreneur / micro (Service / Commerce / Restauration)
function computeMicro(activityType, ca, otherIncome) {
  const isService = activityType === 'service';
  const abattementRate = isService ? 0.5 : 0.29;
  const liberatoireRate = isService ? 0.017 : 0.01;
  const abattementLabel = isService ? '50%' : '29%';
  const liberatoireLabel = isService ? '1,7%' : '1%';

  // Without prélèvement libératoire
  const taxableBase = ca * abattementRate;
  const taxableTotal = taxableBase + otherIncome;
  const withoutLib = progressiveTax(taxableTotal);

  const breakdownWithout = [
    "Type d'activité : " + (isService ? 'Service' : 'Commerce / Restauration'),
    'Revenu imposable = CA × ' + abattementLabel + ' = ' + fmtEur(ca) + ' × ' + abattementLabel + ' = ' + fmtEur(taxableBase),
  ];
  if (otherIncome > 0) {
    breakdownWithout.push('+ Autres revenus = ' + fmtEur(otherIncome));
    breakdownWithout.push('Total imposable = ' + fmtEur(taxableBase) + ' + ' + fmtEur(otherIncome) + ' = ' + fmtEur(taxableTotal));
  }
  for (const s of withoutLib.slices) {
    const upperLabel = s.to === Infinity ? '∞' : fmtEur(s.to);
    breakdownWithout.push('Tranche ' + fmtEur(s.from) + ' → ' + upperLabel + ' : ' + fmtEur(s.slice) + ' × ' + s.rate + '% = ' + fmtEur(s.tax));
  }
  breakdownWithout.push('IMPÔT TOTAL = ' + fmtEur(withoutLib.total));

  // With prélèvement libératoire
  const liberatoireTax = ca * liberatoireRate;
  const otherProgressive = progressiveTax(otherIncome);
  const withLibTotal = liberatoireTax + otherProgressive.total;

  const breakdownWith = [
    "Type d'activité : " + (isService ? 'Service' : 'Commerce / Restauration'),
    'Prélèvement libératoire = CA × ' + liberatoireLabel + ' = ' + fmtEur(ca) + ' × ' + liberatoireLabel + ' = ' + fmtEur(liberatoireTax),
  ];
  if (otherIncome > 0) {
    breakdownWith.push('Autres revenus à imposer au barème progressif : ' + fmtEur(otherIncome));
    for (const s of otherProgressive.slices) {
      const upperLabel = s.to === Infinity ? '∞' : fmtEur(s.to);
      breakdownWith.push('Tranche ' + fmtEur(s.from) + ' → ' + upperLabel + ' : ' + fmtEur(s.slice) + ' × ' + s.rate + '% = ' + fmtEur(s.tax));
    }
    breakdownWith.push('Impôt sur autres revenus = ' + fmtEur(otherProgressive.total));
  }
  breakdownWith.push('IMPÔT TOTAL = ' + fmtEur(liberatoireTax) + (otherIncome > 0 ? ' + ' + fmtEur(otherProgressive.total) : '') + ' = ' + fmtEur(withLibTotal));

  return {
    cards: [
      buildResultCard('Sans prélèvement libératoire', withoutLib.total, breakdownWithout, 'amber'),
      buildResultCard('Avec prélèvement libératoire', withLibTotal, breakdownWith, 'emerald'),
    ],
  };
}

export function computeTax(activityType, ca, hasLiberatoire, otherIncome, pmeRegime, pmeBenefice) {
  if (!activityType) return null;

  if (activityType === 'pme') {
    if (!pmeRegime) return null;
    if (pmeRegime === 'is') {
      return { cards: [computePmeIS(pmeBenefice || 0)] };
    }
    if (pmeRegime === 'ir') {
      return { cards: [computePmeIR(pmeBenefice || 0, otherIncome || 0)] };
    }
    return null;
  }

  return computeMicro(activityType, ca, otherIncome);
}
